import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import api, { hasWrite, isApprover } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VoucherDialog } from "../components/vouchers/VoucherDialog";
import { PaymentReceiptDialog } from "../components/vouchers/PaymentReceiptDialog";
import { DocFilters, DocBulkBar, DocTable } from "../components/vouchers/DocTable";
import { EditDocDialog } from "../components/vouchers/EditDocDialog";
import { askReason, useApprovalLocks, LockNotice } from "../components/ApprovalLock";
import { AttachmentsDialog } from "../components/Attachments";

const exportDocs = (picked, tab) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = ["number", "date", "name", "voucher_type", "employee_name", "amount", "pay_method"];
  const lines = [header.join(",")].concat(picked.map((d) => [d.number, d.date, d.receiver_name, d.voucher_type, d.employee_name, d.amount, d.pay_method].map(esc).join(",")));
  const el = document.createElement("a");
  el.href = URL.createObjectURL(new Blob([lines.join("\n") + "\n"], { type: "text/csv" }));
  el.download = `${tab}_export_${new Date().toISOString().slice(0, 10)}.csv`;
  el.click();
  URL.revokeObjectURL(el.href);
  toast.success(`Exported ${picked.length} row(s)`);
};

const useDocList = () => {
  const [tab, setTab] = useState("voucher");
  const [f, setF] = useState({ start: "", end: "", parties: [] });
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const reqId = useRef(0);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    const params = { doc_type: tab };
    if (f.start) params.start = f.start;
    if (f.end) params.end = f.end;
    if (f.parties.length) params.parties = f.parties.join(",");
    const { data } = await api.get("/vouchers", { params });
    if (id !== reqId.current) return;
    setDocs(data);
    setSelected(new Set());
  }, [tab, f]);
  useEffect(() => { load(); }, [load]);
  return { tab, setTab, f, setF, docs, selected, setSelected, load };
};

export default function Vouchers() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const canApprove = isApprover(user);
  const [locks, reloadLocks] = useApprovalLocks();
  const [lockInfo, setLockInfo] = useState(null);
  const [edit, setEdit] = useState(null);
  const [attFor, setAttFor] = useState(null);
  const [partyOpts, setPartyOpts] = useState([]);
  const { tab, setTab, f, setF, docs, selected, setSelected, load } = useDocList();
  useEffect(() => { api.get("/parties").then((r) => setPartyOpts((r.data || []).map((p) => p.name))).catch((e) => console.error(e)); }, []);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = docs.length > 0 && docs.every((d) => selected.has(d.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(docs.map((d) => d.id)));
  const picked = () => docs.filter((d) => selected.has(d.id));

  const exportSelected = () => (picked().length ? exportDocs(picked(), tab) : toast.error("Select at least one row"));

  const delSelected = async () => {
    const rows = picked();
    if (rows.length === 0) return toast.error("Select at least one row");
    if (!window.confirm(`Delete ${rows.length} selected? Admin deletes immediately; others send a request.`)) return;
    const reason = askReason(user, "deleting", `${rows.length} selected entries`);
    if (reason === null) return;
    let ok = 0, pending = 0, fail = 0;
    for (const d of rows) {
      try { const res = await api.delete(`/vouchers/${d.id}`, { params: { reason } }); res.data?.status === "pending" ? pending++ : ok++; }
      catch { fail++; }
    }
    toast[fail ? "info" : "success"](`${ok} deleted${pending ? `, ${pending} sent for approval` : ""}${fail ? `, ${fail} failed` : ""}`);
    load();
  };

  const del = async (d) => {
    const msg = canApprove ? `Delete ${d.number}? It will also be removed from the cash book.` : `Request deletion of ${d.number}? An admin must approve it.`;
    if (!window.confirm(msg)) return;
    const reason = askReason(user, "deleting", d.number);
    if (reason === null) return;
    const res = await api.delete(`/vouchers/${d.id}`, { params: { reason } });
    if (res.data?.status === "pending") { toast.info(res.data.message || "Sent for approval"); reloadLocks(); }
    else toast.success("Deleted");
    load();
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <PageHeader title="Vouchers & Receipts" subtitle="Issue signed payment vouchers and receipts — both post to the cash book.">
          {canWrite && <div className="flex gap-2"><VoucherDialog onDone={load} /><PaymentReceiptDialog onDone={load} /></div>}
        </PageHeader>
        <DocFilters tab={tab} setTab={setTab} f={f} setF={setF} partyOpts={partyOpts} />
        <DocBulkBar count={selected.size} canWrite={canWrite} onExport={exportSelected} onDelete={delSelected} onClear={() => setSelected(new Set())} />
        <DocTable tab={tab} docs={docs} selected={selected} toggleSel={toggleSel} allSelected={allSelected} toggleAll={toggleAll}
          locks={locks} setLockInfo={setLockInfo} canWrite={canWrite} onEdit={setEdit} onDelete={del} onAttach={setAttFor} />
        <AttachmentsDialog open={!!attFor} onClose={() => setAttFor(null)} entityType="voucher" entityId={attFor?.id}
          title={attFor ? `${attFor.number} · ${attFor.receiver_name || ""}` : ""} onChanged={load} />
        <EditDocDialog doc={edit} onClose={() => setEdit(null)} onSaved={load} user={user} canApprove={canApprove} reloadLocks={reloadLocks} />
      </div>
      <LockNotice info={lockInfo} onClose={() => setLockInfo(null)} />
    </Layout>
  );
}
